/**
 * Used to power the page, query, and collection search results on the search results page.
 */

import { LitElement, html, css } from '@hh/global.js'

class HHPredictiveSearchResults extends LitElement {
  static properties = {
    searchTerm: {
      type: String,
      attribute: 'search-term',
    },
    searchType: {
      type: String,
      attribute: 'search-type',
    },
    noResults: {
      type: Boolean,
      attribute: 'no-results',
      reflect: true,
    },
    cachedResults: {
      type: Object,
    },
  }

  constructor() {
    super()

    this.cachedResults = {}

    this.searchFields = [
      'product_type',
      'tag',
      'title',
      'variants.barcode',
      'variants.sku',
      'variants.title',
      'vendor',
    ]
  }

  get searchResults() {
    return (
      this.shadowRoot
        .querySelector(`slot[name="search-${this.searchType}-results"]`)
        ?.assignedNodes()[0] ?? null
    )
  }

  connectedCallback() {
    super.connectedCallback()
    this.resourceTypes = [this.searchType]

    this.getSearchResults(this.searchTerm)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  async getSearchResults(query) {
    const queryKey = query.replaceAll(' ', '-').toLowerCase()

    if (this.cachedResults?.[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey])
      return
    }

    const searchEndpoint = window.routes.predictive_search_url

    const params = {
      q: query,
      'resources[type]': this.resourceTypes.join(','),
      'resources[options][fields]': this.searchFields.join(','),
      section_id: `search-results-${this.searchType}`,
    }

    const paramsString = new URLSearchParams(params).toString()
    const searchUrl = `${searchEndpoint}?${paramsString}`

    try {
      const res = await fetch(searchUrl)
      if (!res.ok) throw new Error(res.status)

      const text = await res.text()
      const resultsMarkup = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector(`#shopify-section-search-results-${this.searchType}`)

      this.cachedResults[queryKey] = resultsMarkup

      this.renderSearchResults(resultsMarkup)
    } catch (error) {
      console.error('An error occurred in the predictive search request: ', {
        error,
        query,
        searchUrl,
      })
    }
  }

  renderSearchResults(resultsMarkup) {
    this.noResults = resultsMarkup.querySelector('.js-no-results') !== null

    resultsMarkup.querySelectorAll('[data-search-term]').forEach((el) => {
      el.innerHTML = el.innerHTML.replace(
        new RegExp(this.searchTerm, 'gi'),
        (match) => `<mark>${match}</mark>`
      )
    })
    this.searchResults.innerHTML = resultsMarkup.innerHTML
    document
      .querySelectorAll('[data-featured-link]')
      .forEach((nonAnchorLink) => {
        nonAnchorLink.addEventListener('click', () => {
          const link = nonAnchorLink.getAttribute('data-featured-link')
          window.location.href = link
        })
      })
    const results_text = resultsMarkup.querySelector('.js-results-count-value')
    if (results_text) {
      document.querySelector('.js-results-count').innerHTML =
        results_text.innerHTML
    }

    setTimeout(() => {
      document
        .querySelector('.loader')
        .classList.remove(`hidden-${this.searchType}`)

      if (
        !document.querySelector('.loader').classList.contains('hidden-page') &&
        !document
          .querySelector('.loader')
          .classList.contains('hidden-collection') &&
        !document.querySelector('.loader').classList.contains('hidden-query')
      ) {
        document.querySelector('.loader').style.display = 'none'
      }
    }, 1000)
  }

  render() {
    return html`
      ${this.searchType == 'query'
        ? html`
            <div>
              <slot name="search-query-results"></slot>
            </div>
          `
        : this.searchType == 'page'
        ? html`<div>
            <slot name="search-page-results"></slot>
          </div>`
        : html`<div>
            <slot name="search-collection-results"></slot>
          </div>`}
    `
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }
  `
}

customElements.define('hh-predictive-search-results', HHPredictiveSearchResults)
